import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { RadioGroup, RadioGroupItem } from './ui/RadioGroup';
import { cn } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';
import type {
  MultipleSurveyQuestion,
  Survey,
  SurveyQuestion,
} from 'posthog-js'; // Import types from posthog-js

// Local interface extending SurveyQuestion for type guard, assuming structure
// Ideally, import the specific subtype from posthog-js if available

// Type guard function
function isSingleChoiceQuestion(
  question: SurveyQuestion
): question is MultipleSurveyQuestion {
  return (
    question.type === 'single_choice' &&
    Array.isArray((question as MultipleSurveyQuestion).choices)
  );
}

interface PosthogSurveyProps {
  rootElementId: string;
  onSubmit: (surveyId: string, answers: Record<string, string | null>) => void;
  className?: string;
  onCancel?: () => void;
  isLoading?: boolean;
}

const PosthogSurvey: React.FC<PosthogSurveyProps> = ({
  rootElementId,
  onSubmit,
  className,
  onCancel,
  isLoading,
}) => {
  const posthog = usePostHog();
  const [surveyConfig, setSurveyConfig] = useState<Survey | null>(null); // Use Survey type
  const [answers, setAnswers] = useState<Record<string, string | null>>({});
  const [openChoiceText, setOpenChoiceText] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    posthog.getActiveMatchingSurveys((surveys: Survey[]) => {
      if (surveys && surveys.length > 0) {
        setSurveyConfig(surveys[0]);
      } else {
        setSurveyConfig(null);
      }
    }, true);
  }, [posthog]);

  // Use optional chaining for question.id as it might be undefined per library types
  const handleChoiceChange = (
    questionId: string | undefined,
    choice: string | null,
    isOtherChoice: boolean = false
  ) => {
    if (!questionId) return;
    const valueToSet =
      isOtherChoice && choice === 'Other'
        ? openChoiceText[questionId] || 'Other'
        : choice;
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: valueToSet,
    }));
    // If selecting a non-"Other" choice, clear the open text for that question
    if (!isOtherChoice && openChoiceText[questionId]) {
      setOpenChoiceText((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
    }
  };

  // Use optional chaining for question.id
  const handleOpenChoiceTextChange = (
    questionId: string | undefined,
    text: string
  ) => {
    if (!questionId) return;
    setOpenChoiceText((prev) => ({
      ...prev,
      [questionId]: text,
    }));
    // Ensure the main answer state reflects the typed text when "Other" is selected
  };

  const handleSubmit = () => {
    if (!surveyConfig) return;

    const finalAnswers: Record<string, string | null> = {};
    surveyConfig.questions?.forEach((question) => {
      if (!question.id) return; // Skip if question has no id
      const qid = question.id;
      const finalQid = `$survey_response_${qid}`;
      const answer = answers[qid];

      if (
        isSingleChoiceQuestion(question) &&
        question.hasOpenChoice &&
        question.choices.at(-1) === answer
      ) {
        finalAnswers[finalQid] = openChoiceText[qid] || 'Other';
      } else {
        finalAnswers[finalQid] = answer;
      }
    });
    console.log('Final Survey Answers for Submission:', finalAnswers);
    onSubmit(surveyConfig.id, finalAnswers);
  };

  if (!surveyConfig) {
    return (
      <div
        id={rootElementId}
        className={cn('p-5 text-muted-foreground', className)}
      >
        No matching surveys found.
      </div>
    );
  }

  // Use optional chaining for questions array and potentially question.buttonText
  const submitButtonText = surveyConfig.questions?.[0]?.buttonText || 'Submit';

  return (
    <div id={rootElementId} className={cn('w-full h-full', className)}>
      {/* Use optional chaining for surveyConfig properties */}
      {surveyConfig.description && (
        <p className="text-muted-foreground mb-4">{surveyConfig.description}</p>
      )}

      {/* Use optional chaining for questions array */}
      {surveyConfig.questions?.map((question) => {
        return (
          <div key={question.id ?? `question-${Math.random()}`}>
            <label className="font-semibold block mb-3 text-foreground">
              {question.question}
            </label>
            {question.description && (
              <p className="text-sm text-muted-foreground -mt-2 mb-3">
                {question.description}
              </p>
            )}

            {/* Use the type guard */}
            {isSingleChoiceQuestion(question) && (
              <RadioGroup
                name={question.id}
                value={question.id ? answers[question.id] ?? '' : ''}
                onValueChange={(value) =>
                  handleChoiceChange(question.id, value)
                }
                className="space-y-2"
              >
                {question.choices.map((choice) => (
                  <div key={choice} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={choice}
                      id={`${question.id ?? 'unidentified'}-${choice}`}
                    />
                    <label
                      htmlFor={`${question.id ?? 'unidentified'}-${choice}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {choice}
                    </label>
                  </div>
                ))}
                {question.hasOpenChoice &&
                  question.id &&
                  question.choices.at(-1) === answers[question.id] && (
                    <Input
                      type="text"
                      placeholder="Please specify"
                      value={
                        question.id ? openChoiceText[question.id] || '' : ''
                      }
                      onChange={(e) =>
                        handleOpenChoiceTextChange(question.id, e.target.value)
                      }
                      disabled={
                        !!question.id &&
                        !(question.choices.at(-1) === answers[question.id])
                      }
                      className="ml-6 w-[calc(100%-24px)] mt-1"
                    />
                  )}
              </RadioGroup>
            )}

            {/* Show placeholder for non-single_choice types */}
            {!isSingleChoiceQuestion(question) && question.type && (
              <p>
                <i>
                  Question type &apos;{question.type}&apos; not yet supported.
                </i>
              </p>
            )}
          </div>
        );
      })}

      <Button
        onClick={handleSubmit}
        className="w-full mt-4"
        disabled={
          surveyConfig.questions.some(
            (q) => !q.optional && (!q.id || !answers[q.id])
          ) || isLoading
        }
      >
        {submitButtonText}
      </Button>
      {onCancel && (
        <Button
          variant="outline"
          className="w-full mt-2"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      )}
    </div>
  );
};

export default PosthogSurvey;
